import { Directive, TemplateRef, ViewChild, ViewContainerRef } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Overlay } from "ol";
import { fromLonLat, toLonLat } from "ol/proj";
import { GlobalVars } from "../global-vars";
import { DataHydro, HydroHubeau } from "../models/hydro-hubeau";
import { IPSData } from "../models/ips-data";
import { Layer } from "../models/layer";
import { Sensor } from "../models/sensor";
import { AuthService } from "../services/auth.service";
import { LayerService } from "../services/layer.service";
import { MapService } from "../services/map.service";
import { SensorService } from "../services/sensor.service";
import { SharedDateService } from "../services/shared-date.service";
import { SidebarService } from "../services/sidebar.service";
import { SymbologyService } from "../services/symbology.service";
import { finalize, forkJoin, tap } from "rxjs";

@Directive()
export abstract class MapAbstractComponent {

    ipsDataOfSelectedSensor!: IPSData;
    selectedDate!: Date;
    selectedScenario!: string;

    tentavPrev!: Layer;
    prevLayerTest!: string;
    lastModeleLayer: string = '';

    lastSectorLayer: string = '';
    previousLayerState: Layer = {
        bsn: false,
        modeles: false,
        piezometres: false,
        hydrometrie: false,
        pluviometres: false,
    }

    previousOverlay: Overlay | null = null
    overlays: Overlay[] = [];

    hasModalForHeadband: boolean = false;
    isLoadingBsn: boolean = false;
    isLoadingSensors: boolean = false;

    @ViewChild('iconsContainer', { read: ViewContainerRef }) iconsContainer!: ViewContainerRef;
    @ViewChild('iconTemplate', { read: TemplateRef }) iconTemplate!: TemplateRef<any>;


    constructor(
        protected sensorService: SensorService,
        protected layerService: LayerService,
        protected sidebarService: SidebarService,
        protected mapService: MapService,
        protected sharedDateService: SharedDateService,
        protected symbologyService: SymbologyService,
        protected authService: AuthService,
        protected translate: TranslateService
    ) { }

    isDateInFuture(date: Date): boolean {
        if (date) {
            const today = new Date();
            return date > today;
        }
        return false;
    }

    getPreviousMonth(date: Date): Date {
        const newDate = new Date(date);
        newDate.setMonth(newDate.getMonth() - 1);
        return newDate;
    }

    // Permet d'ajouter un point (capteur) sur la map avec des infos spécifiques en fonction du type de capteurs et de la couche
    addIconToMap(data: (IPSData | Sensor | DataHydro)[], layerType: string): void {
        this.clearOverlays(layerType);
        if (this.mapService.map) {
            const currentZoom = this.mapService.map.getView().getZoom();
            data.forEach((sensor: IPSData | Sensor | DataHydro | any) => {
                let iconDetails;
                if (this.symbologyService.isIPSData(sensor) && currentZoom) {
                    iconDetails = this.symbologyService.processIPSData(sensor as IPSData, currentZoom);
                } else if (layerType === 'hydrometrie') {
                    iconDetails = this.symbologyService.processHydrometrie(sensor);
                } else if (layerType === 'pluviometres') {
                    iconDetails = this.symbologyService.processPluvio(sensor);
                } else {
                    iconDetails = this.symbologyService.processGenericSensor(sensor as Sensor, layerType);
                }
                const { coordinates, icon, fontSize, textColor, backgroundColor, border, cursor, padding, transform } = iconDetails;

                const sensorData = {
                    icon: icon,
                    fontSize: fontSize,
                    textColor: textColor,
                    backgroundColor: backgroundColor,
                    border: border,
                    cursor: cursor,
                    padding: padding,
                    transform: transform,
                    type: layerType,
                    ...(layerType !== 'hydrometrie' && layerType !== 'pluviometres' ? { dataType: sensor.type_Donnee.type } : {})
                };

                const customMapIcon = this.iconsContainer.createEmbeddedView(this.iconTemplate, { $implicit: { capteur: sensorData } });

                const iconElement = customMapIcon.rootNodes[0] as HTMLElement;
                iconElement.classList.add(layerType);
                if (this.symbologyService.isIPSData(sensor)) {
                    iconElement.addEventListener('click', () => {
                        this.displayHeadband(sensor as IPSData);
                    });
                }
                const iconOverlay = new Overlay({
                    element: iconElement,
                    autoPan: false,
                });
                iconOverlay.setPosition(coordinates);
                if (this.mapService.map) {
                    this.mapService.map.addOverlay(iconOverlay);
                }
                this.overlays.push(iconOverlay);
                this.isLoadingSensors = false;
            });
        }
    }

    addOverlaySvg(coordinates: number[], sensor: IPSData): void {
        if (this.previousOverlay && this.mapService.map) {
            this.mapService.map.removeOverlay(this.previousOverlay);
        }
        const iconUrl = 'assets/images/map-marker.svg';
        const overlayIcon = document.createElement('img');
        overlayIcon.src = iconUrl;
        overlayIcon.style.width = '50px';
        overlayIcon.style.height = '50px';
        overlayIcon.style.position = 'absolute';
        overlayIcon.style.transform = sensor.modelId ? 'translate(-5%, -80%)' : 'translate(-21%, -80%)'

        const iconOverlay = new Overlay({
            position: coordinates,
            positioning: 'center-center',
            element: overlayIcon
        });
        if (this.mapService.map)
            this.mapService.map.addOverlay(iconOverlay);

        const overlayElement = iconOverlay.getElement();
        if (overlayElement && overlayElement.parentNode) {
            const overlayContainer = overlayElement.parentNode as HTMLElement;
            overlayContainer.style.zIndex = '3'; // Ensure it's on top
        }
        this.overlays.push(iconOverlay);

        this.previousOverlay = iconOverlay;
    }

    // Permet de clear une couche spécifique de données (capteurs, modèles)
    clearOverlays(layerType?: string): void {
        if (layerType) {
            this.overlays = this.overlays.filter(overlay => {
                const element = overlay.getElement();
                if (element && element.classList.contains(layerType)) {
                    if (this.mapService.map)
                        this.mapService.map.removeOverlay(overlay);
                    return false;
                }
                return true;
            });
        } else {
            this.overlays = this.overlays.filter(overlay => {
                const element = overlay.getElement();
                if (element && element.classList.contains("modeles")) {
                    return true;
                }
                if (this.mapService.map)
                    this.mapService.map.removeOverlay(overlay);
                return false;
            });
        }
    }

    // Vérifie et Clear les couches qui ont été enlevé par rapport à l'état précédent
    protected removeDisabledOverlays(currentLayerState: Layer): void {
        Object.keys(this.previousLayerState).forEach(key => {
            const currentState = currentLayerState[key as keyof Layer];
            const previousState = this.previousLayerState[key as keyof Layer];
            if (previousState === true && currentState === false) {
                this.clearOverlays(key);
            }
        });
        if (currentLayerState.modeles == false && currentLayerState.piezometres == false) {
            this.sensorService.cancelFetchPiezoSensorRequest()
        }
        if (this.mapService.sectorLayer && !currentLayerState.bsn) {
            this.mapService.updateIconsBasedOnZoom(this.refreshSector);
            this.mapService.sectorLayer.getSource()?.clear();
        }
    }

    // Gère l'affiche du bandeau latéral
    displayHeadband(sensor: IPSData) {
        this.sidebarService.setSidebarStatus(true);
        this.hasModalForHeadband = !!sensor.modelId;
        this.ipsDataOfSelectedSensor = sensor;
        const coordinates = fromLonLat(sensor.geometry.geometry.coordinates);
        this.addOverlaySvg(coordinates, sensor);
    }

    // Permet de reset l'affichage de la map à son état initial
    resetView(): void {
        this.clearOverlays();
        this.clearOverlays("modeles")
        this.mapService.resetView();
        if (this.lastSectorLayer === GlobalVars.sectorLayersType.detailed) {
            this.mapService.sectorLayer.getSource()?.clear();
            this.mapService.removeSectorIcons();
            const statusReset = this.sharedDateService.getResetDateValue()
            this.sharedDateService.updateResetDate(!statusReset)
        }
    }

    async refreshSector(forceRefresh: boolean = false) {
        const layerTypeToDisplay = this.mapService.getSectorLayerType();

        if (this.mapService.sectorLayer && (this.lastSectorLayer !== layerTypeToDisplay || !this.previousLayerState.bsn || layerTypeToDisplay === GlobalVars.sectorLayersType.detailed || forceRefresh)) {
            this.isLoadingBsn = true;
            const zoomRefresh = layerTypeToDisplay === GlobalVars.sectorLayersType.detailed;
            this.lastSectorLayer = layerTypeToDisplay;

            let dateToFetch = this.selectedDate;
            let olFeatures: string | any[] = [];

            let iterations = 0;

            while (iterations < 12) {
                const wfsUrl = this.mapService.createSectorWfsUrl(dateToFetch, zoomRefresh);
                olFeatures = await this.mapService.fetchWfsData(wfsUrl, this.selectedDate);

                if (olFeatures.length > 0) {
                    break;
                }

                // Passer au mois précédent
                dateToFetch = this.getPreviousMonth(dateToFetch);
                iterations++;
            }

            // Si aucune donnée trouvée après 12 mois, retour et clear des données
            if (olFeatures.length === 0) {
                const source = this.mapService.sectorLayer.getSource();
                if (source) {
                    this.mapService.removeSectorIcons();
                    source.clear();
                }
                this.isLoadingBsn = false;
                return;
            }

            if (olFeatures.length > 0) {
                const source = this.mapService.sectorLayer.getSource();
                if (source) {
                    this.mapService.removeSectorIcons();
                    source.clear();
                    source.addFeatures(olFeatures);
                    this.mapService.sectorLayer.setStyle(this.symbologyService.setSectorLayerStyle(layerTypeToDisplay));
                }
            } else {
                const source = this.mapService.sectorLayer.getSource();
                if (source) {
                    this.mapService.removeSectorIcons();
                    source.clear();
                }
            }

            this.isLoadingBsn = false;
        }

        if (this.isDateInFuture(this.selectedDate)) {
            const source = this.mapService.sectorLayer.getSource();
            if (source) {
                this.mapService.removeSectorIcons();
                source.clear();
            }
        }
    }

    // Vérifie le zoom et les couches activées à chaque mouvement sur la map afin de rafraichir l'affichage des capteurs
    protected checkZoomAndFetchSensors(forceReloadBsn?: boolean, forceReloadUser?: boolean): void {
        if (this.mapService.map) {
            const currentZoom = this.mapService.map.getView().getZoom();
            const layerTypeToDisplay = this.mapService.getSectorLayerType();
            const minZoomLevelForSensors = GlobalVars.wfsSimplifiedMaxZoomAndSensors;
            const layerState = this.layerService.getLayerState();
            let updateDisabledLayerState = layerState;

            const isBsnActive = layerState.bsn;

            if (isBsnActive) {
                this.refreshSector(forceReloadBsn);
            }

            // Si la map n'est pas assez zoomer pour afficher les capteurs -> stop des requêtes en cours + clear des capteurs sur la map
            if (currentZoom && currentZoom < minZoomLevelForSensors) {

                if (updateDisabledLayerState != this.tentavPrev || this.prevLayerTest != layerTypeToDisplay || forceReloadUser) {
                    if (this.authService.isGlobalAdmin() || this.authService.isBrgm()) {
                        updateDisabledLayerState.modelesDisabled = false;
                        updateDisabledLayerState.modeles = true;
                    } else {
                        updateDisabledLayerState.modelesDisabled = true;
                        updateDisabledLayerState.modeles = false;
                    }

                    updateDisabledLayerState.piezometres = false;
                    updateDisabledLayerState.hydrometrieDisabled = true;
                    updateDisabledLayerState.piezometresDisabled = true;
                    updateDisabledLayerState.pluviometresDisabled = true;
                    this.tentavPrev = updateDisabledLayerState;
                    this.prevLayerTest = layerTypeToDisplay;
                    this.layerService.updateLayerState(updateDisabledLayerState)
                }


                this.sensorService.cancelRequests();
                this.clearOverlays();
                //this.isLoadingSensors = false;

            }

            if (currentZoom && currentZoom > minZoomLevelForSensors) {
                if (updateDisabledLayerState != this.tentavPrev || this.prevLayerTest != layerTypeToDisplay || forceReloadUser) {
                    updateDisabledLayerState.modelesDisabled = false;
                    updateDisabledLayerState.hydrometrieDisabled = false;
                    updateDisabledLayerState.piezometresDisabled = false;
                    updateDisabledLayerState.pluviometresDisabled = false;
                    updateDisabledLayerState.modeles = true;
                    updateDisabledLayerState.piezometres = true;
                    this.tentavPrev = updateDisabledLayerState;
                    this.prevLayerTest = layerTypeToDisplay;
                    this.layerService.updateLayerState(updateDisabledLayerState)
                }
            }

            const isModelesActive = updateDisabledLayerState.modeles;
            const isPiezometresActive = updateDisabledLayerState.piezometres;
            const isPluviometresActive = updateDisabledLayerState.pluviometres;
            const isHydrometrieActive = updateDisabledLayerState.hydrometrie;
            // Si le zoom est assez grand et qu'au moins une couche est activée ->  Fetch les capteurs des couches actives et les affiches
            if (isModelesActive || isPiezometresActive || isHydrometrieActive || isHydrometrieActive) {
                const extent = this.mapService.map.getView().calculateExtent(this.mapService.map.getSize());
                const bottomLeft = [extent[0], extent[1]];
                const topRight = [extent[2], extent[3]];
                const [minX, minY] = toLonLat(bottomLeft);
                const [maxX, maxY] = toLonLat(topRight);
                let bboxParams = `ymin=${minY}&ymax=${maxY}&xmin=${minX}&xmax=${maxX}`;
                const bboxParamsHubeau = `${minX},${minY},${maxX},${maxY}`;
                // Créer un tableau d'observables pour toutes les requêtes nécessaires
                const requests = [];

                // Requête pour les modèles et piézomètres
                if (isModelesActive || isPiezometresActive) {
                    let scenarioParam = '';
                    if (this.selectedDate && this.sharedDateService.isDateInFuture(this.selectedDate)) {
                        scenarioParam = `&scenario=${this.selectedScenario}`;
                    }
                    const dateParam = this.selectedDate && !this.sharedDateService.isDateToday(this.selectedDate) ? `&date=${this.selectedDate.toISOString().split('T')[0]}` : '';
                    if (layerTypeToDisplay === GlobalVars.sectorLayersType.simplified) {
                        bboxParams = GlobalVars.bbboxFrance;
                    }
                    if (layerTypeToDisplay != this.lastModeleLayer || layerTypeToDisplay === GlobalVars.sectorLayersType.detailed || !this.previousLayerState.modeles) {
                        this.sensorService.cancelFetchPiezoSensorRequest(),
                            requests.push(
                                this.sensorService.fetchPiezoSensorForMap(`${bboxParams}${dateParam}${scenarioParam}`).pipe(
                                    tap((res: IPSData[]) => {
                                        const piezoWithModelId = res.filter(sensor => sensor.modelId);
                                        const piezoWithoutModelId = res.filter(sensor => !sensor.modelId);
                                        if (layerState.modeles) {
                                            this.lastModeleLayer = layerTypeToDisplay;
                                            this.addIconToMap(piezoWithModelId, 'modeles');
                                        }
                                        if (layerState.piezometres && currentZoom && currentZoom >= minZoomLevelForSensors) {
                                            this.addIconToMap(piezoWithoutModelId, 'piezometres');
                                        }
                                    })
                                )
                            );
                    }

                }

                // Requête pour les pluviomètres
                if (isPluviometresActive && currentZoom && currentZoom >= minZoomLevelForSensors) {
                    requests.push(
                        this.sensorService.fetchSensorForMap('Pluie', bboxParams).pipe(
                            tap((pluviometres: Sensor[]) => {
                                const filteredPluviometres = pluviometres.filter((pluie) => !pluie.identifier.includes('ERA5'));
                                if (layerState.pluviometres) {
                                    this.addIconToMap(filteredPluviometres, 'pluviometres');
                                }
                            })
                        )
                    );
                }

                // Requête pour l'hydrométrie
                if (isHydrometrieActive && currentZoom && currentZoom >= minZoomLevelForSensors) {
                    requests.push(
                        this.sensorService.fetchHydroSensorHubeau(bboxParamsHubeau).pipe(
                            tap((hydrometrie: HydroHubeau) => {
                                if (layerState.hydrometrie) {
                                    this.addIconToMap(hydrometrie.data, 'hydrometrie');
                                }
                            })
                        )
                    );
                }
                // Utiliser forkJoin pour attendre que toutes les requêtes soient terminées
                if (requests.length > 0) {
                    this.isLoadingSensors = true;

                    forkJoin(requests).pipe(
                        finalize(() => {
                            this.isLoadingSensors = false;
                        })
                    ).subscribe({
                        complete: () => {
                            this.previousLayerState = { ...layerState };
                        },
                        error: () => {
                            this.isLoadingSensors = false;
                        }
                    });
                }
            }
        } else {
            this.clearOverlays();
        }

    }

    // Vérifie quelles couches ont été ajouté par rapport à l'état précédent
    protected shouldReloadData(currentLayerState: Layer): boolean {
        if (!this.previousLayerState) {
            this.previousLayerState = { ...currentLayerState };
            return true;
        }
        const layersAdded = Object.keys(currentLayerState).some(key => {
            const currentState = currentLayerState[key as keyof Layer];
            const previousState = this.previousLayerState![key as keyof Layer];
            return previousState === false && currentState === true;
        });
        return layersAdded;
    }

    // Prend connaissance des couches à afficher ou non
    protected updateLayersVisibility(currentLayerState: Layer): void {
        const shouldReload = this.shouldReloadData(currentLayerState);
        this.removeDisabledOverlays(currentLayerState);
        if (shouldReload) {
            this.checkZoomAndFetchSensors();
        }
        this.previousLayerState = { ...currentLayerState };
    }

}